import { ActorSystem, ErrorActorEvent, sendTo } from 'xstate';
import { BRAND_CONFIGS } from '~/config/branding';
import { sendAxiosErrorAlert } from '~/features/utils';
import { ALERT_VIEW_STATE } from '~/features/xstateSystem';

// [MCV-1889] The coded errors the B2B sell side can answer with. Each says why the call was refused,
// so the user is told what to do instead of reading a raw "HTTP Exception". The API puts the code
// under `extra.code` (or, on some bodies, at the top), always on the status listed here.
export type ListingErrorCode =
  // 403: selling listings are rolled out per employer.
  | 'FEATURE_DISABLED'
  // 403: a fit can only be measured against the caller's own buying listing.
  | 'COMPARE_LISTING_FORBIDDEN'
  // 429: the employer's daily fit checks are spent.
  | 'FIT_QUOTA_EXCEEDED'
  // 422
  | 'NO_ROSTER'
  | 'COVERAGE_INVALID'
  | 'FACILITIES_NOT_ALLOWED'
  | 'OFFERING_ONLY'
  // 422: a fit is checked against the caller's own buying listing.
  | 'SEEKING_LISTING_REQUIRED'
  // 400: a request for a seller's labor attaches no providers.
  | 'PROVIDER_IDS_NOT_ALLOWED'
  // 409
  | 'IMMUTABLE_FIELD'
  | 'CONTRACT_EXISTS'
  | 'LISTING_FILLED'
  | 'LISTING_NOT_FILLED'
  | 'LISTING_CANCELLED'
  | 'LISTING_NOT_CANCELLED'
  | 'ALREADY_RELISTED';

/** Kept for the callers that only ever dealt with the 409s. */
export type ListingConflictCode = Extract<
  ListingErrorCode,
  | 'CONTRACT_EXISTS'
  | 'LISTING_FILLED'
  | 'IMMUTABLE_FIELD'
  | 'LISTING_NOT_FILLED'
  | 'LISTING_CANCELLED'
  | 'LISTING_NOT_CANCELLED'
  | 'ALREADY_RELISTED'
>;

// B2B listings are an HLX surface, so this is HLX's in-app support address.
export const SUPPORT_EMAIL = BRAND_CONFIGS.hlx.supportEmail;

// What the server's own `detail` says about the daily limit; shown as the server wrote it.
export const FIT_QUOTA_MESSAGE = 'Fit checks are used up for today. Resets at midnight UTC.';

const LISTING_ERROR_STATUS: Record<ListingErrorCode, number> = {
  FEATURE_DISABLED: 403,
  COMPARE_LISTING_FORBIDDEN: 403,
  FIT_QUOTA_EXCEEDED: 429,
  NO_ROSTER: 422,
  COVERAGE_INVALID: 422,
  FACILITIES_NOT_ALLOWED: 422,
  OFFERING_ONLY: 422,
  SEEKING_LISTING_REQUIRED: 422,
  PROVIDER_IDS_NOT_ALLOWED: 400,
  IMMUTABLE_FIELD: 409,
  CONTRACT_EXISTS: 409,
  LISTING_FILLED: 409,
  LISTING_NOT_FILLED: 409,
  LISTING_CANCELLED: 409,
  LISTING_NOT_CANCELLED: 409,
  ALREADY_RELISTED: 409,
};

const LISTING_ERROR_COPY: Record<ListingErrorCode, { text: string; subText: string }> = {
  FEATURE_DISABLED: {
    text: "Selling listings aren't available yet",
    subText: `They haven't been turned on for your account. Contact ${SUPPORT_EMAIL} if you'd like access.`,
  },
  COMPARE_LISTING_FORBIDDEN: {
    text: "Can't compare to that listing",
    subText: 'Fit can only be compared to one of your own buying listings. Choose another listing.',
  },
  FIT_QUOTA_EXCEEDED: {
    text: 'Fit checks used up',
    subText: FIT_QUOTA_MESSAGE,
  },
  NO_ROSTER: {
    text: 'Nothing to check yet',
    subText: "This listing has no providers to check. The seller hasn't attached any.",
  },
  COVERAGE_INVALID: {
    text: 'Coverage needs attention',
    subText:
      'Add the states and the location with a distance that match this listing’s work location, then try again.',
  },
  FACILITIES_NOT_ALLOWED: {
    text: "Selling listings don't use facilities",
    subText: 'Set the states and locations you cover instead.',
  },
  OFFERING_ONLY: {
    text: 'Only for selling listings',
    subText: 'Offered providers and coverage locations belong to selling listings.',
  },
  SEEKING_LISTING_REQUIRED: {
    text: 'Choose a buying listing first',
    subText: 'Fit is measured against one of your own buying listings. Choose one to compare.',
  },
  PROVIDER_IDS_NOT_ALLOWED: {
    text: "Providers can't be attached",
    subText:
      "A selling listing's roster is what is on offer, so a request for it attaches no providers.",
  },
  IMMUTABLE_FIELD: {
    text: "That can't be changed",
    subText:
      "A listing's side and its active and filled status can't be edited. Use the listing actions instead.",
  },
  CONTRACT_EXISTS: {
    text: "This listing can't be cancelled here",
    subText: `It was filled through a contract. Cancelling it cancels the contract, so contact ${SUPPORT_EMAIL}.`,
  },
  LISTING_FILLED: {
    text: 'This listing is already filled',
    subText: "A filled listing can't be activated or paused.",
  },
  LISTING_NOT_FILLED: {
    text: "This listing isn't filled",
    subText: 'Only a filled listing can be cancelled. Deactivate it to take it out of search.',
  },
  LISTING_CANCELLED: {
    text: 'This listing is already cancelled',
    subText: 'Refresh to see its current status.',
  },
  LISTING_NOT_CANCELLED: {
    text: "This listing can't be relisted yet",
    subText: 'Only a cancelled listing can be relisted.',
  },
  ALREADY_RELISTED: {
    text: 'This listing was already relisted',
    subText: 'Look for its new draft in your listings.',
  },
};

const SERVER_WORDED = new Set<ListingErrorCode>(['FIT_QUOTA_EXCEEDED', 'COVERAGE_INVALID']);

type ErrorResponse = {
  status?: number;
  data?: { code?: unknown; detail?: unknown; extra?: { code?: unknown } };
};

const errorResponse = (error: unknown): ErrorResponse | undefined =>
  error != null && typeof error === 'object' && 'response' in error
    ? (error as { response?: ErrorResponse }).response
    : undefined;

/** The sell-side code of a refused call; it may sit at the top of the body or under `extra`. */
export const listingErrorCode = (error: unknown): ListingErrorCode | null => {
  const response = errorResponse(error);
  const code = response?.data?.extra?.code ?? response?.data?.code;
  if (typeof code !== 'string' || !Object.prototype.hasOwnProperty.call(LISTING_ERROR_STATUS, code))
    return null;
  return LISTING_ERROR_STATUS[code as ListingErrorCode] === response?.status
    ? (code as ListingErrorCode)
    : null;
};

/** The 409 codes alone (cancel, relist, activate and update refusals). */
export const listingConflictCode = (error: unknown): ListingConflictCode | null => {
  const code = listingErrorCode(error);
  return code != null && LISTING_ERROR_STATUS[code] === 409 ? (code as ListingConflictCode) : null;
};

// PUT /providers answers 422 with `detail: { invalidProviderIds }` (no code) when an added provider
// is not actively associated with the employer.
const refusesRosterProviders = (error: unknown): boolean => {
  const response = errorResponse(error);
  const detail = response?.data?.detail;
  return (
    response?.status === 422 &&
    detail != null &&
    typeof detail === 'object' &&
    Array.isArray((detail as { invalidProviderIds?: unknown }).invalidProviderIds)
  );
};

export const listingErrorAlert = (event: { error?: unknown }) => {
  const code = listingErrorCode(event.error);
  if (!code && refusesRosterProviders(event.error)) {
    return {
      type: 'ON_SHOW_ALERT' as const,
      payload: {
        text: "Some providers can't be offered",
        subText:
          'Only providers with an active association with your employer can be on a roster. Remove the others and save again.',
        type: 'error' as const,
        timeout: 8000,
      },
    };
  }
  if (!code) return sendAxiosErrorAlert(event);
  const copy = LISTING_ERROR_COPY[code];
  // These two are worded by the server (the limit names its reset; coverage says what is missing).
  const detail = errorResponse(event.error)?.data?.detail;
  const subText =
    SERVER_WORDED.has(code) && typeof detail === 'string' && detail.trim() ? detail : copy.subText;
  return {
    type: 'ON_SHOW_ALERT' as const,
    payload: { ...copy, subText, type: 'error' as const, timeout: 8000 },
  };
};

// Same as the shared axios alert, with the sell-side refusals above spelled out.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const showListingErrorAction: any = sendTo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ system }: { system: ActorSystem<any> }) => system?.get(ALERT_VIEW_STATE),
  ({ event }: { event: ErrorActorEvent<unknown> }) => listingErrorAlert(event)
);
